/*
==================================================
AUREALM FOOTER
Version: 2.4.4
Footer Accordions + Newsletter Validation + Verified Multi-Endpoint Submit
==================================================
*/

(function () {
  'use strict';

  const footer = document.querySelector('[data-au-footer]');

  if (!footer) return;

  if (footer.dataset.footerReady === 'true') return;
  footer.dataset.footerReady = 'true';

  const groups = footer.querySelectorAll('[data-au-footer-group]');
  const forms = footer.querySelectorAll('.au-footer__newsletter-form');

  /*
  ==================================================
  KLAVIYO LIST SUBSCRIBE (CLIENT API)
  Footer signups join the newsletter list for real.
  Mirrors aurealm-journal-newsletter.js: identify/track
  alone does NOT add a profile to a list — this does.
  ==================================================
  */

  const KLAVIYO_COMPANY_ID =
    footer.getAttribute('data-klaviyo-company-id') || 'RcCHqB';
  const KLAVIYO_LIST_ID =
    footer.getAttribute('data-klaviyo-list-id') || 'Yiif9v';

  function subscribeToList(email) {
    if (!KLAVIYO_COMPANY_ID || !KLAVIYO_LIST_ID || !email) {
      return Promise.resolve({ subscribed: false, reason: 'missing-config' });
    }

    const endpoint =
      'https://a.klaviyo.com/client/subscriptions/?company_id=' +
      encodeURIComponent(KLAVIYO_COMPANY_ID);

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Footer Newsletter',
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email }
            }
          }
        },
        relationships: {
          list: {
            data: { type: 'list', id: KLAVIYO_LIST_ID }
          }
        }
      }
    };

    return fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: '2024-10-15'
      },
      body: JSON.stringify(payload)
    }).then(function (response) {
      // Client API returns 202 Accepted on success (no body).
      if (response.status === 202 || response.ok) {
        return { subscribed: true };
      }
      throw new Error('Klaviyo subscribe failed: HTTP ' + response.status);
    });
  }

  /*
  ==================================================
  MOBILE ACCORDIONS
  ==================================================
  */

  function isMobile() {
    return window.matchMedia('(max-width: 720px)').matches;
  }

  /*
    A collapsed panel used to be hidden by max-height:0 + opacity:0 and nothing
    else. That hides it visually but leaves every link inside it in the tab
    order. Measured at 390px: groups 2 and 3 held 11 and 4 links, all with
    tabIndex >= 0, and calling .focus() on the first succeeded — focus landed on
    "Crystal Guide" inside a zero-height, zero-opacity container. A keyboard user
    tabbing out of the footer crossed 15 invisible stops with no visible focus
    indicator (WCAG 2.4.3, 2.4.7), and no toggle carried aria-expanded (4.1.2).

    `inert` rather than `hidden` or `visibility:hidden` — it takes the subtree
    out of the tab order and the accessibility tree without touching layout, so
    the max-height transition still animates.

    State goes through one function called from every path that can change it,
    because there are four, not two: open, close, resize up to desktop, and
    resize down to mobile. Patching only closeGroup/openGroup would leave a
    desktop visitor who narrows their window with three collapsed but fully
    focusable panels — the same defect by a different route.
  */
  function applyGroupState(group, isOpen, mobile) {
    const panel = group.querySelector('[data-au-footer-panel]');
    const toggle = group.querySelector('[data-au-footer-toggle]');

    // Above 720px there is no accordion: every panel is laid out and visible,
    // so nothing is ever inert and the toggle always reports expanded.
    const expanded = mobile ? isOpen : true;

    if (toggle) {
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    if (!panel) return;

    if (expanded) {
      panel.removeAttribute('inert');
    } else {
      panel.setAttribute('inert', '');
    }
  }

  function closeGroup(group) {
    const panel = group.querySelector('[data-au-footer-panel]');

    group.classList.remove('is-open');

    if (panel) {
      panel.style.maxHeight = '0px';
    }

    applyGroupState(group, false, isMobile());
  }

  function openGroup(group) {
    const panel = group.querySelector('[data-au-footer-panel]');

    group.classList.add('is-open');

    if (panel) {
      panel.style.maxHeight = `${panel.scrollHeight}px`;
    }

    applyGroupState(group, true, isMobile());
  }

  function resetDesktop() {
    groups.forEach(function (group) {
      const panel = group.querySelector('[data-au-footer-panel]');

      group.classList.remove('is-open');

      if (panel) {
        panel.style.maxHeight = '';
      }

      applyGroupState(group, false, false);
    });
  }

  /*
    Crossing 720px downwards. resetDesktop has stripped is-open from every group,
    so the CSS collapses all three and none of them would be inert. Re-establish
    the mobile default that initFooterAccordions sets on a fresh load: first
    group open, the rest closed and inert.
  */
  function resetMobile() {
    groups.forEach(function (group, index) {
      if (index === 0) {
        openGroup(group);
      } else {
        closeGroup(group);
      }
    });
  }

  function initFooterAccordions() {
    groups.forEach(function (group, index) {
      const toggle = group.querySelector('[data-au-footer-toggle]');

      if (!toggle) return;

      if (toggle.dataset.footerToggleReady === 'true') return;
      toggle.dataset.footerToggleReady = 'true';

      if (isMobile()) {
        // Every group needs its state set, not just the one being opened.
        // Previously groups 1 and 2 were never touched at init, so they were
        // collapsed by CSS while staying focusable and reporting no state.
        if (index === 0) {
          openGroup(group);
        } else {
          closeGroup(group);
        }
      } else {
        applyGroupState(group, false, false);
      }

      toggle.addEventListener('click', function () {
        if (!isMobile()) return;

        const isOpen = group.classList.contains('is-open');

        groups.forEach(closeGroup);

        if (!isOpen) {
          openGroup(group);
        }
      });
    });
  }

  /*
  ==================================================
  NEWSLETTER VALIDATION + NO-RELOAD SUBMIT
  ==================================================
  */

  function initNewsletterValidation() {
    if (!forms.length) return;

    const blockedDomains = [
      'test.com',
      'text.com',
      'example.com',
      'mailinator.com',
      'fake.com',
      'temp-mail.com',
      'tempmail.com'
    ];

    forms.forEach(function (form) {
      const input = form.querySelector('input[type="email"]');
      const button = form.querySelector('button');
      const status = form.querySelector('[data-au-footer-newsletter-status]');

      if (!input || !button) return;

      if (form.dataset.footerFormReady === 'true') return;
      form.dataset.footerFormReady = 'true';

      const defaultButtonText = button.textContent.trim() || 'Enter the Realm';
      const successMessage =
        footer.getAttribute('data-newsletter-success') ||
        'You are now part of the realm.';
      const errorMessage =
        footer.getAttribute('data-newsletter-error') ||
        'We could not complete your signup. Please try again.';

      function setStatus(message, state) {
        if (!status) return;

        status.textContent = message || '';
        status.classList.toggle('is-success', state === 'success');
        status.classList.toggle('is-error', state === 'error');
        status.setAttribute('aria-live', state === 'error' ? 'assertive' : 'polite');
      }

      function validateEmail(value) {
        const email = String(value || '').trim().toLowerCase();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailRegex.test(email)) return false;

        const domain = email.split('@')[1];

        if (!domain) return false;

        return !blockedDomains.includes(domain);
      }

      function updateState() {
        const hasValue = input.value.trim().length > 0;
        const isValid = validateEmail(input.value);

        if (!form.classList.contains('is-success')) {
          button.disabled = !isValid;
        }

        input.setAttribute(
          'aria-invalid',
          String(!isValid && hasValue)
        );
      }

      function trackFooterNewsletterSubmit(email) {
        if (!email) return;

        window._learnq = window._learnq || [];

        window._learnq.push([
          'identify',
          {
            '$email': email,
            'Aurealm Footer Newsletter Signup': true,
            'Aurealm Signup Source': 'Footer Newsletter'
          }
        ]);

        window._learnq.push([
          'track',
          'Aurealm Footer Newsletter Submitted',
          {
            source: 'Footer Newsletter',
            form_location: 'Footer',
            page_url: window.location.href,
            submitted_at: new Date().toISOString()
          }
        ]);
      }

      async function submitToShopifySilently() {
        const formData = new FormData(form);

        /*
          Shopify customer form action normally reloads the page.
          This fetch submits it in background instead.
        */
        const response = await fetch(form.action || window.location.pathname, {
          method: 'POST',
          body: formData,
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) {
          throw new Error('Shopify newsletter submit failed: HTTP ' + response.status);
        }

        return { subscribed: true };
      }

      async function handleSubmit(event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const email = String(input.value || '').trim().toLowerCase();

        if (!validateEmail(email)) {
          input.setAttribute('aria-invalid', 'true');
          input.focus();
          return false;
        }

        footer.classList.add('is-newsletter-submitting');
        form.classList.add('is-submitting');
        form.classList.remove('is-success');
        setStatus('', 'idle');

        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = 'Entering...';

        trackFooterNewsletterSubmit(email);

        try {
          /*
            Submit to both newsletter endpoints and verify their outcomes.
            The customer sees success when at least one endpoint confirms
            the subscription; a failure is shown only when neither does.
          */
          const results = await Promise.allSettled([
            subscribeToList(email),
            submitToShopifySilently()
          ]);

          const klaviyoSucceeded =
            results[0].status === 'fulfilled' &&
            results[0].value &&
            results[0].value.subscribed === true;
          const shopifySucceeded =
            results[1].status === 'fulfilled' &&
            results[1].value &&
            results[1].value.subscribed === true;

          if (!klaviyoSucceeded && !shopifySucceeded) {
            throw new Error('All footer newsletter endpoints failed.');
          }

          if (window.AUREALM_DEBUG) {
            if (!klaviyoSucceeded) {
              console.warn('Aurealm footer Klaviyo subscribe did not succeed.', results[0]);
            }
            if (!shopifySucceeded) {
              console.warn('Aurealm footer Shopify submit did not succeed.', results[1]);
            }
          }

          form.classList.remove('is-submitting');
          form.classList.add('is-success');

          input.value = '';
          input.setAttribute('aria-invalid', 'false');
          setStatus(successMessage, 'success');

          button.setAttribute('aria-busy', 'false');
          button.disabled = true;
          button.textContent = successMessage;
        } catch (error) {
          form.classList.remove('is-submitting');
          form.classList.remove('is-success');
          setStatus(errorMessage, 'error');

          button.setAttribute('aria-busy', 'false');
          button.disabled = false;
          button.textContent = defaultButtonText;

          window.AUREALM_DEBUG && console.warn('Aurealm footer newsletter submit failed.', error);
        } finally {
          footer.classList.remove('is-newsletter-submitting');
          updateState();
        }

        return false;
      }

      input.addEventListener('input', function () {
        if (status && status.classList.contains('is-error')) {
          setStatus('', 'idle');
        }
        updateState();
      });
      input.addEventListener('blur', updateState);

      /*
        Capture phase is important here because Shopify/theme scripts
        may also listen for customer-form submit.
      */
      form.addEventListener('submit', handleSubmit, true);

      updateState();
    });
  }

  /*
  ==================================================
  INIT
  ==================================================
  */

  /*
    Both directions matter. The old handler only fired going up to desktop, so
    a desktop visitor narrowing their window landed on three CSS-collapsed
    panels that were still fully focusable. Tracked with a flag so the work only
    happens when the breakpoint is actually crossed, not on every resize tick.
  */
  let wasMobile = isMobile();

  window.addEventListener('resize', function () {
    const nowMobile = isMobile();

    if (nowMobile === wasMobile) return;
    wasMobile = nowMobile;

    if (nowMobile) {
      resetMobile();
    } else {
      resetDesktop();
    }
  });

  initFooterAccordions();
  initNewsletterValidation();
})();